<template>
  <div class="entra-page">
    <EntraPageHeader
      title="Licences"
      :tenantDisplayName="tenantName"
      :primaryDomain="primaryDomain"
      :regionCode="regionCode"
      :tenantStatus="tenantStatus"
      :freshnessState="freshnessState"
      :lastCollection="lastCollection"
      :dataCompleteness="dataCompleteness"
      :capabilities="capabilities"
      @refresh="loadData"
      @export="handleExport"
    />

    <div class="page-body">
      <div class="kpi-grid">
        <MetricCard label="Active Subscriptions" :value="summary.activeSubscriptions" :state="summaryState" />
        <MetricCard label="Purchased Units" :value="summary.purchasedUnits" :state="summaryState" />
        <MetricCard label="Consumed Units" :value="summary.consumedUnits" :state="summaryState" />
        <MetricCard label="Available Units" :value="summary.availableUnits" :state="summaryState" />
        <MetricCard label="Overall Utilisation" :value="formatPercentage(summary.overallUtilisation)" :state="summaryState" />
        <MetricCard label="Est. Monthly Cost" :value="formatCurrency(summary.estimatedMonthlyCost)" :state="summaryState" />
        <MetricCard label="Est. Annual Cost" :value="formatCurrency(summary.estimatedAnnualCost)" :state="summaryState" />
          <MetricCard label="Est. Monthly Saving" :value="formatCurrency(summary.estimatedMonthlySaving)" :state="summaryState" trend-direction="down" trend-label="Opportunity" />
        <MetricCard label="Disabled Licensed" :value="summary.disabledLicensedUsers" :state="summaryState" />
        <MetricCard label="Inactive Licensed" :value="summary.inactiveLicensedUsers" :state="summaryState" />
        <MetricCard label="Direct Assignments" :value="summary.directAssignments" :state="summaryState" />
        <MetricCard label="Assignment Errors" :value="summary.assignmentErrors" :state="summaryState" />
      </div>

      <div v-if="error" class="error-message">{{ error }}</div>

      <h2 class="section-title">Licence Utilisation</h2>

      <div v-if="tableState === 'loading'" class="loading-state">Loading licences...</div>
      <div v-else-if="tableState === 'empty'" class="empty-wrapper">
        <EmptyState title="No licences found" description="No subscribed SKUs found for this tenant." />
      </div>
      <div v-else class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>SKU</th>
              <th>Status</th>
              <th>Purchased</th>
              <th>Consumed</th>
              <th>Available</th>
              <th>Utilisation</th>
              <th>Utilisation Bar</th>
              <th>Assigned Users</th>
              <th>Monthly Cost</th>
              <th>Potential Saving</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="sku in skus" :key="sku.skuId">
              <td class="product-name">{{ sku.friendlyName || sku.skuPartNumber }}</td>
              <td class="sku-part">{{ sku.skuPartNumber }}</td>
              <td>
                <span class="sku-status" :class="'sku-status-' + (sku.capabilityStatus || 'unknown')">{{ sku.capabilityStatus || 'Unknown' }}</span>
              </td>
              <td>{{ formatCount(sku.enabledUnits) }}</td>
              <td>{{ formatCount(sku.consumedUnits) }}</td>
              <td>{{ formatCount((sku.enabledUnits || 0) - (sku.consumedUnits || 0)) }}</td>
              <td :style="{ color: utilisationColor(sku) }">{{ formatPercentage(skuUtilisation(sku)) }}</td>
              <td>
                <div class="inline-bar-container">
                  <div class="inline-bar" :style="{ width: skuUtilisation(sku) + '%', backgroundColor: barColor(skuUtilisation(sku)) }"></div>
                </div>
              </td>
              <td>{{ formatCount(sku.assignedUsers) }}</td>
              <td>--</td>
              <td class="saving-cell">--</td>
              <td>
                <button class="detail-btn" @click="openSku(sku.skuId)">Details</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="opportunities.length > 0" class="opportunities-section">
        <h2 class="section-title">Optimisation Opportunities</h2>
        <div class="opportunities-grid">
          <div v-for="opp in opportunities" :key="opp.type" class="opportunity-card">
            <FindingSeverityChip :severity="opp.severity" />
            <div class="opportunity-info">
              <div class="opportunity-title">{{ opp.title }}</div>
              <div class="opportunity-detail">{{ opp.affectedCount }} users &middot; {{ formatCurrency(opp.estimatedSaving, opp.currency) }}/mo</div>
            </div>
            <button class="detail-btn" @click="goToRecommendations">View</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import MetricCard from '@/components/entra/common/MetricCard.vue';
import EmptyState from '@/components/entra/common/EmptyState.vue';
import FindingSeverityChip from '@/components/entra/common/FindingSeverityChip.vue';
import { getEntraLicenceSummary, getEntraSkus, getEntraLicenceOpportunities } from '@/services/entra-api.js';
import { formatCurrency, formatPercentage, formatCount } from '@/services/entra-utils.js';

export default {
  name: 'EntraLicences',
  components: { EntraPageHeader, MetricCard, EmptyState, FindingSeverityChip },
  data: function() {
    return {
      tenantId: '', tenantName: '', primaryDomain: '', regionCode: '', tenantStatus: '',
      freshnessState: 'fresh', lastCollection: '', dataCompleteness: null, capabilities: [],
      summary: { activeSubscriptions: 0, purchasedUnits: 0, consumedUnits: 0, availableUnits: 0, overallUtilisation: null, estimatedMonthlyCost: 0, estimatedAnnualCost: 0, estimatedMonthlySaving: 0, disabledLicensedUsers: 0, inactiveLicensedUsers: 0, directAssignments: 0, assignmentErrors: 0 },
      summaryState: 'loading',
      skus: [], tableState: 'loading',
      opportunities: [],
      error: ''
    };
  },
  methods: {
    formatCurrency: formatCurrency, formatPercentage: formatPercentage, formatCount: formatCount,
    skuUtilisation: function(sku) {
      if (!sku || !sku.enabledUnits || sku.enabledUnits <= 0) return 0;
      return (sku.consumedUnits || 0) / sku.enabledUnits * 100;
    },
    utilisationColor: function(sku) {
      var u = this.skuUtilisation(sku);
      if (u >= 95) return '#dc2626';
      if (u >= 80) return '#ea580c';
      return '#16a34a';
    },
    barColor: function(u) {
      if (u >= 95) return '#dc2626';
      if (u >= 80) return '#ea580c';
      if (u <= 30) return '#ca8a04';
      return '#16a34a';
    },
    goToRecommendations: function() {
      if (this.tenantId) {
        this.$router.push('/entra/tenants/' + encodeURIComponent(this.tenantId) + '/recommendations');
      }
    },
    openSku: function() {},
    handleExport: function(format) { console.log('Export as', format); },
    loadData: function() {
      var self = this;
      this.tenantId = this.$route.params.tenantId || '';
      if (!this.tenantId) {
        this.error = 'Please select a tenant from the Portfolio page first.';
        this.summaryState = 'empty';
        this.tableState = 'empty';
        return;
      }

      getEntraLicenceSummary(this.tenantId).then(function(data) {
        self.summary = {
          activeSubscriptions: data.activeSubscriptions || 0,
          purchasedUnits: data.purchasedUnits || 0,
          consumedUnits: data.consumedUnits || 0,
          availableUnits: data.availableUnits || 0,
          overallUtilisation: data.overallUtilisation,
          estimatedMonthlyCost: data.estimatedMonthlyCost || 0,
          estimatedAnnualCost: data.estimatedAnnualCost || 0,
          estimatedMonthlySaving: data.estimatedMonthlySaving || 0,
          disabledLicensedUsers: data.disabledLicensedUsers || 0,
          inactiveLicensedUsers: data.inactiveLicensedUsers || 0,
          directAssignments: data.directAssignments || 0,
          assignmentErrors: data.assignmentErrors || 0
        };
        self.summaryState = 'loaded';
      }).catch(function(err) { self.error = err.message; });

      getEntraSkus(this.tenantId).then(function(data) {
        self.skus = data.items || data || [];
        self.tableState = self.skus.length === 0 ? 'empty' : 'loaded';
      }).catch(function(err) { self.error = err.message; self.tableState = 'error'; });

      getEntraLicenceOpportunities(this.tenantId).then(function(data) {
        self.opportunities = data.items || data || [];
      }).catch(function() {});
    }
  },
  mounted: function() { this.loadData(); },
  watch: { '$route.params.tenantId': 'loadData' }
};
</script>

<style scoped>
.entra-page { min-height: 100%; }
.page-body { padding: 24px; }

.kpi-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 12px; margin-bottom: 24px; }
.section-title { font-size: 1rem; font-weight: 600; color: #1e293b; margin: 24px 0 12px 0; }

.error-message { padding: 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; color: #991b1b; font-size: 0.875rem; }
.loading-state { padding: 48px; text-align: center; color: #94a3b8; }

.table-wrapper { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
.data-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
.data-table th { background: #f8fafc; color: #475569; font-weight: 600; text-align: left; padding: 10px 12px; border-bottom: 2px solid #e2e8f0; white-space: nowrap; }
.data-table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #334155; }

.product-name { font-weight: 600; color: #1e293b; }
.sku-part { font-size: 0.6875rem; color: #94a3b8; font-family: monospace; }
.sku-status { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 0.6875rem; font-weight: 600; }
.sku-status-enabled { background: #dcfce7; color: #166534; }
.sku-status-warning { background: #fef3c7; color: #92400e; }
.sku-status-suspended { background: #fee2e2; color: #991b1b; }
.sku-status-unknown { background: #f1f5f9; color: #475569; }

.inline-bar-container { width: 100px; height: 6px; background: #f1f5f9; border-radius: 3px; overflow: hidden; }
.inline-bar { height: 100%; border-radius: 3px; }

.saving-cell { color: #16a34a; font-weight: 500; }

.detail-btn { background: none; border: 1px solid #cbd5e1; padding: 4px 10px; border-radius: 4px; font-size: 0.75rem; color: #475569; cursor: pointer; font-family: 'Inter', sans-serif; }
.detail-btn:hover { background: #f1f5f9; }

.opportunities-section { margin-top: 24px; }
.opportunities-grid { display: flex; flex-direction: column; gap: 8px; }
.opportunity-card { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; }
.opportunity-info { flex: 1; min-width: 0; }
.opportunity-title { font-size: 0.8125rem; font-weight: 600; color: #1e293b; }
.opportunity-detail { font-size: 0.75rem; color: #64748b; margin-top: 2px; }
</style>
